import React, { useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type { AssetPerformanceDTO } from '../types';
import { TrendBadge } from './TrendBadge';
import { RangeSlider } from './RangeSlider';
import { AssetChartModal } from './AssetChartModal';

const columnHelper = createColumnHelper<AssetPerformanceDTO>();

interface AssetTableProps {
  data: AssetPerformanceDTO[];
}

export const AssetTable: React.FC<AssetTableProps> = ({ data }) => {
  // The asset whose chart modal is currently open (null = modal closed).
  const [selectedAsset, setSelectedAsset] =
    useState<AssetPerformanceDTO | null>(null);

  // useMemo caches these definitions. They only compile once when the
  // component mounts.
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Asset',
        cell: (info) => (
          <button
            type="button"
            onClick={() => setSelectedAsset(info.row.original)}
            className="flex flex-col text-left group focus:outline-none"
            title={`View ${info.getValue()} price chart`}
          >
            <span className="font-semibold text-blue-600 dark:text-blue-400 group-hover:underline">
              {info.getValue()}
            </span>
            <span className="text-xs text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300">
              {info.row.original.symbol}
            </span>
          </button>
        ),
      }),
      columnHelper.accessor('actual_value', {
        header: () => <div className="text-right">Last Price</div>,
        cell: (info) => (
          <div className="text-right font-mono text-gray-800 dark:text-gray-200">
            {info.getValue().toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        ),
      }),
      columnHelper.accessor('one_day_change_pct', {
        header: () => <div className="text-right">1D Change</div>,
        cell: (info) => (
          <div className="text-right">
            <TrendBadge value={info.getValue()} />
          </div>
        ),
      }),
      columnHelper.accessor('ytd_change_pct', {
        header: () => <div className="text-right">YTD Change</div>,
        cell: (info) => (
          <div className="text-right">
            <TrendBadge value={info.getValue()} />
          </div>
        ),
      }),
      columnHelper.accessor('position_in_5y_range', {
        header: '5-Year Range (Low \u2194 High)',
        cell: (info) => (
          <RangeSlider
            low={info.row.original.five_year_low}
            high={info.row.original.five_year_high}
            current={info.row.original.actual_value}
            position={info.getValue()}
          />
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Safe fallback as gracefully dictated by the router.py design (the
  // backend returns empty arrays rather than a 404).
  if (data.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        No market data available.
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white dark:bg-gray-900 dark:border-gray-800">
        <table className="w-full text-sm text-left">
        <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 uppercase text-xs border-b border-gray-200 dark:border-gray-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="px-6 py-4 font-semibold whitespace-nowrap"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        </table>
      </div>

      {/* On-demand price/volume chart modal for the clicked asset. */}
      {selectedAsset && (
        <AssetChartModal
          symbol={selectedAsset.symbol}
          name={selectedAsset.name}
          onClose={() => setSelectedAsset(null)}
        />
      )}
    </>
  );
};
